import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTrips } from '../../context/TripsContext';
import { useExpenses } from '../../context/ExpensesContext';
import { ExpenseRow, ExpensesHeader } from './components';
import { ExpenseCardMobile } from './components/ExpenseCardMobile';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { LoadingView } from '@/components';
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

type SortField = 'date' | 'amount';
type SortDirection = 'asc' | 'desc' | null;

export const ExpensesPage = (): React.ReactNode => {
  const navigate = useNavigate();
  const { currentTrip } = useTrips();
  const { expenses, loading, error, fetchTripExpenses } = useExpenses();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);

  useEffect(() => {
    if (currentTrip?.id) {
      fetchTripExpenses(currentTrip.id);
    }
  }, [currentTrip?.id, fetchTripExpenses]);

  const handleAddExpense = () => {
    navigate(`/trip/${currentTrip?.short_id}/add-expense`);
  };

  // Get unique categories
  const categories = useMemo(() => {
    const uniqueCategories = Array.from(new Set(expenses.map(e => e.category)));
    return uniqueCategories.sort();
  }, [expenses]);

  // Handle sorting
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      // Cycle through: asc -> desc -> null
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        setSortDirection(null);
        setSortField(null);
      }
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort expenses
  const filteredAndSortedExpenses = useMemo(() => {
    let result = [...expenses];

    // Filter by category
    if (selectedCategory) {
      result = result.filter(e => e.category === selectedCategory);
    }

    // Sort
    if (sortField && sortDirection) {
      result.sort((a, b) => {
        let comparison = 0;
        if (sortField === 'date') {
          comparison = new Date(a.expense_date).getTime() - new Date(b.expense_date).getTime();
        } else if (sortField === 'amount') {
          comparison = a.amount - b.amount;
        }
        return sortDirection === 'asc' ? comparison : -comparison;
      });
    }

    return result;
  }, [expenses, selectedCategory, sortField, sortDirection]);

  if (error) return <div>Error: {error}</div>;
  if (loading) return <LoadingView />;

  return (
    <div className="flex flex-col gap-6">
      {/* Expenses Header with Metrics */}
      <ExpensesHeader expenses={expenses} onAddExpense={handleAddExpense} />

      {/* All Expenses Section Title */}
      <h2 className="text-3xl font-bold tracking-tight">All Expenses</h2>

      {/* Expenses Table Card */}
      <Card className="md:block border-0 md:border shadow-none md:shadow-sm">
        <CardContent className="p-0 md:p-6">
          {expenses.length === 0 ? (
            <div className="text-center text-muted-foreground py-12">
              No expenses recorded yet
            </div>
          ) : (
            <>
              {/* Filter by Category */}
              <div className="mb-4 flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">Filter by category:</span>
                <Badge
                  variant={selectedCategory === null ? 'default' : 'outline'}
                  className="cursor-pointer"
                  onClick={() => setSelectedCategory(null)}
                >
                  All ({expenses.length})
                </Badge>
                {categories.map(category => (
                  <Badge
                    key={category}
                    variant={selectedCategory === category ? 'default' : 'outline'}
                    className="cursor-pointer"
                    onClick={() => setSelectedCategory(category)}
                  >
                    {category} ({expenses.filter(e => e.category === category).length})
                  </Badge>
                ))}
              </div>

              {/* Mobile View */}
              <div className="md:hidden space-y-3">
                {filteredAndSortedExpenses.length === 0 ? (
                  <div className="text-center text-muted-foreground py-12">
                    No expenses found for the selected filter
                  </div>
                ) : (
                  filteredAndSortedExpenses.map((expense) => (
                    <ExpenseCardMobile
                      key={expense.id}
                      expense={expense}
                      onClick={() => {
                        // Could navigate to expense detail or expand inline
                      }}
                    />
                  ))
                )}
              </div>

              {/* Desktop View */}
              <div className="hidden md:block">
                {/* Table Header */}
                <div className="flex items-center gap-4 px-4 py-3 bg-muted/50 rounded-t-lg border-b font-medium text-sm">
                  {/* Date with Sort */}
                  <div className="w-24 flex-shrink-0 flex items-center gap-1">
                    <span>Date</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleSort('date')}
                    >
                      {sortField === 'date' && sortDirection === 'asc' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : sortField === 'date' && sortDirection === 'desc' ? (
                        <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3" />
                      )}
                    </Button>
                  </div>

                  {/* Category */}
                  <div className="w-32 flex-shrink-0">Category</div>

                  {/* Description */}
                  <div className="flex-1 min-w-0">Description</div>

                  {/* Amount with Sort */}
                  <div className="w-32 flex-shrink-0 text-right flex items-center justify-end gap-1">
                    <span>Amount</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleSort('amount')}
                    >
                      {sortField === 'amount' && sortDirection === 'asc' ? (
                        <ArrowUp className="h-3 w-3" />
                      ) : sortField === 'amount' && sortDirection === 'desc' ? (
                        <ArrowDown className="h-3 w-3" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3" />
                      )}
                    </Button>
                  </div>

                  {/* Actions placeholder */}
                  <div className="w-16"></div>
                </div>

                {/* Expenses List */}
                <div className="border rounded-b-lg overflow-hidden">
                  {filteredAndSortedExpenses.length === 0 ? (
                    <div className="text-center text-muted-foreground py-12">
                      No expenses found for the selected filter
                    </div>
                  ) : (
                    filteredAndSortedExpenses.map((expense) => (
                      <ExpenseRow key={expense.id} expense={expense} />
                    ))
                  )}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

